import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link } from "react-router-dom";

import { api, ApiError, type DrawCeremony, type DrawCeremonyPlan, type TournamentMatch } from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";
import {
  buildDrawCeremonyLines,
  drawLineBandText,
  drawLinePlayerText,
} from "../lib/drawCeremonyLines";
import { makeScopedLatestOnly, type ScopedToken } from "../lib/latestOnly";
import { escapeHtml, openPrintWindow } from "../lib/printTemplate";

/** Sorteo del cuadro (estándar internacional, 2026-07-15): el admin anuncia
 * la hora, el sistema registra el resultado al generar el draw y los
 * jugadores ven ambos. Este archivo tiene la tarjeta admin (programar +
 * resultado) y el bloque presentacional que también usa la vista jugador. */

const METHOD_LABELS: Record<string, string> = {
  itf_standard: "Sorteo estándar internacional (ITF/USTA)",
  random: "Cuadro 100% por sorteo",
  manual: "Draw armado manualmente por el organizador",
  prelim: "Ronda con pases directos: descansos a los mejores sembrados",
  rr_groups: "Sorteo de grupos: sembrados en serpentina y S/R por sorteo",
};

export function ceremonyMethodLabel(method: string | null | undefined): string {
  if (!method) return "Sorteo del cuadro";
  return METHOD_LABELS[method] ?? "Sorteo del cuadro";
}

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 16);
}

/** GATE VISUAL SINCRÓNICO (6º gate de #135): TODO el estado visible de la
 * tarjeta pasa por esta derivación pura. Si lo cargado no pertenece a la
 * identidad de ESTE render (división, refreshKey o época de sesión
 * distintas), la tarjeta se comporta como "cargando" en el MISMO frame, sin
 * esperar efectos: sin acta, sin resultados, sin draft, sin mensajes, sin
 * imprimir y sin guardar — un acta de A jamás se pinta ni se imprime bajo
 * encabezado o partidos de B. Pura y exportada para regresionarla sin
 * montar la Card. */
export interface DrawCeremonyLoadState {
  /** Identidad (torneo:división|refreshKey) bajo la que terminó el load. */
  identity: string;
  /** Sesión EXACTA bajo la que terminó el load (9º gate de #135): igualdad
   * estricta de strings — sin hash, cero colisiones posibles. Vive solo en
   * memoria del componente; jamás se renderiza ni se registra. */
  sessionToken: string;
  status: "loaded" | "error";
}

/** Lista de partidos CON DUEÑO (8º gate de #135): el padre estampa, al
 * resolver su fetch, bajo qué sesión/torneo/división cargó la lista. */
export interface DrawCeremonyMatchesSnapshot {
  matches: TournamentMatch[];
  owner: { token: string; tournamentId: number; divisionId: number | null } | null;
}

/** Ownership atómico: los partidos solo se usan (pantalla y PDF) si su dueño
 * es EXACTAMENTE el scope vigente de la tarjeta — sesión, torneo y división.
 * En el frame transicional división A→B (ceremonia B cargada, lista aún de
 * A) owned=false: el acta mixta es inconstruible. Sin snapshot (contrato
 * previo, acta solo de bandas) owned=true con lista vacía. */
export function drawCeremonyMatchesForScope(
  snapshot: DrawCeremonyMatchesSnapshot | undefined,
  current: { token: string; tournamentId: number; divisionId: number | null },
): { owned: boolean; matches: TournamentMatch[] } {
  if (!snapshot) return { owned: true, matches: [] };
  const owner = snapshot.owner;
  const owned =
    owner !== null &&
    owner.token === current.token &&
    owner.tournamentId === current.tournamentId &&
    (owner.divisionId ?? null) === (current.divisionId ?? null);
  return { owned, matches: owned ? snapshot.matches : [] };
}

export function deriveDrawCeremonyView(state: {
  renderIdentity: string;
  /** Sesión vigente de ESTE render — se compara por igualdad estricta contra
   * la sesión estampada en load/save (9º gate: sin hash, sin colisiones). */
  sessionToken: string;
  load: DrawCeremonyLoadState | null;
  savingOp: { identity: string; sessionToken: string } | null;
  ceremony: DrawCeremony | null;
  scheduledDraft: string;
  message: string | null;
  error: string | null;
  /** ¿La lista de partidos pertenece al scope vigente? (ownership atómico) */
  matchesOwned: boolean;
}): {
  scopeLoaded: boolean;
  /** El load de ESTA identidad falló: se muestra el error (y nada más). */
  scopeFailed: boolean;
  /** Ni cargada ni fallida bajo esta identidad: la tarjeta está "cargando". */
  loading: boolean;
  saving: boolean;
  ceremony: DrawCeremony | null;
  scheduledDraft: string;
  message: string | null;
  error: string | null;
  canSave: boolean;
  canPrint: boolean;
} {
  // Pertenencia por IGUALDAD ESTRICTA de identidad Y sesión (9º gate): dos
  // sesiones distintas jamás pueden confundirse — no hay hash de por medio.
  const own =
    state.load !== null &&
    state.load.identity === state.renderIdentity &&
    state.load.sessionToken === state.sessionToken;
  const scopeLoaded = own && state.load!.status === "loaded";
  // Un load FALLIDO de la identidad vigente debe seguir mostrando su error
  // (gates previos); uno de otra identidad, jamás.
  const scopeFailed = own && state.load!.status === "error";
  const saving =
    state.savingOp !== null &&
    state.savingOp.identity === state.renderIdentity &&
    state.savingOp.sessionToken === state.sessionToken;
  const ceremony = scopeLoaded ? state.ceremony : null;
  return {
    scopeLoaded,
    scopeFailed,
    loading: !scopeLoaded && !scopeFailed,
    saving,
    ceremony,
    scheduledDraft: scopeLoaded ? state.scheduledDraft : "",
    message: scopeLoaded ? state.message : null,
    error: scopeLoaded || scopeFailed ? state.error : null,
    canSave: scopeLoaded && !saving,
    // Imprimir exige además que la lista de partidos pertenezca al scope
    // vigente (8º gate): ceremonia y matches del MISMO dueño, o no hay PDF.
    canPrint: scopeLoaded && ceremony?.performed_at != null && state.matchesOwned,
  };
}

/** ¿La hora anunciada (naive CDMX) ya pasó? Se compara contra la hora CDMX
 * REAL — no contra el reloj local del navegador, que adelantaría/atrasaría
 * el letrero una hora para quien vea desde otra zona (p.ej. Cancún). */
export function isPastCdmx(scheduledAtIso: string): boolean {
  const nowCdmx = new Date()
    .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
    .replace(" ", "T");
  return scheduledAtIso.slice(0, 16) < nowCdmx.slice(0, 16);
}

export function DrawCeremonyResult({ ceremony }: { ceremony: DrawCeremony }) {
  const bands = ceremony.payload?.bands ?? [];
  const isGroupDraw = ceremony.method === "rr_groups";
  const drawnGroups = ceremony.payload?.groups ?? [];
  const groupChanges = ceremony.payload?.post_draw_changes ?? [];
  return (
    <div className="draw-ceremony__result">
      <p className="draw-ceremony__status">
        {ceremony.performed_at ? (
          <>
            <strong>Sorteo realizado</strong> el {formatDateTime(ceremony.performed_at)} h (CDMX) ·{" "}
            {ceremonyMethodLabel(ceremony.method)}
            {isGroupDraw
              ? ` · ${ceremony.seed_count ?? 0} sembrado(s) y ${ceremony.payload?.unseeded_count ?? 0} por sorteo`
              : ceremony.seed_count
                ? ` · ${ceremony.seed_count} sembrado(s) en cuadro de ${ceremony.draw_size}`
                : null}
          </>
        ) : ceremony.scheduled_at ? (
          isPastCdmx(ceremony.scheduled_at) ? (
            <>
              El sorteo estaba <strong>programado para el {formatDateTime(ceremony.scheduled_at)} h (CDMX)</strong>.
              El resultado se publica en cuanto el organizador genere el cuadro.
            </>
          ) : (
            <>
              <strong>Sorteo programado</strong> para el {formatDateTime(ceremony.scheduled_at)} h (CDMX)
            </>
          )
        ) : (
          "Sorteo pendiente de programar."
        )}
      </p>
      {ceremony.payload?.standard ? (
        <p className="muted draw-ceremony__standard">{ceremony.payload.standard}</p>
      ) : null}
      {bands.length > 0 ? (
        <div className="responsive-table-wrap">
          <table className="data-table draw-ceremony__table">
            <thead>
              <tr>
                <th>Banda</th>
                <th>Sembrado</th>
                <th>Jugador</th>
                <th className="data-table__cell--num">Línea del cuadro</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((band) =>
                band.seeds.map((row, index) => (
                  <tr key={`${band.band}-${row.seed}`}>
                    {index === 0 ? (
                      <td rowSpan={band.seeds.length}>
                        {band.band}
                        {band.drawn ? " · sorteada" : " · fija"}
                      </td>
                    ) : null}
                    <td>#{row.seed}</td>
                    <td>{row.display_name}</td>
                    <td className="data-table__cell--num">{row.line ?? "—"}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      ) : null}
      {ceremony.performed_at && ceremony.payload?.unseeded_count && !isGroupDraw ? (
        <p className="muted">
          {ceremony.payload.unseeded_count} jugador(es) no sembrado(s) entraron 100% por sorteo en las líneas restantes.
        </p>
      ) : null}
      {isGroupDraw && drawnGroups.length > 0 ? (
        <div className="responsive-table-wrap">
          <table className="data-table draw-ceremony__table">
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Jugador</th>
                <th>Seed</th>
                <th>Cómo entró</th>
              </tr>
            </thead>
            <tbody>
              {drawnGroups.map((group) =>
                group.members.map((member, index) => (
                  <tr key={`${group.group_id}-${member.participant_id}`}>
                    {index === 0 ? <td rowSpan={group.members.length}>{group.name}</td> : null}
                    <td>{member.display_name}</td>
                    <td>{member.seed != null ? `#${member.seed}` : "S/R"}</td>
                    <td>{member.via === "serpentina" ? "Serpentina" : "Sorteo"}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      ) : null}
      {isGroupDraw && groupChanges.length > 0 ? (
        <div className="draw-ceremony__changes">
          <p>
            <strong>{groupChanges.length} cambio(s) después del sorteo</strong>
          </p>
          <ul>
            {groupChanges.map((change, index) => (
              <li key={`${change.participant_id}-${index}`} className="muted">
                {formatDateTime(change.at)} h · {change.display_name}:{" "}
                {change.from_group && change.to_group
                  ? `de ${change.from_group} a ${change.to_group}`
                  : change.to_group
                    ? `entró a ${change.to_group}`
                    : change.from_group
                      ? `salió de ${change.from_group}`
                      : "cambio de grupo"}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default function DrawCeremonyAdminCard({
  token,
  tournamentId,
  divisionId,
  refreshKey,
  matchesSnapshot,
  tournamentName,
  divisionName,
  organizerLogoUrl,
}: {
  token: string;
  tournamentId: number;
  divisionId: number | null;
  refreshKey?: string | number;
  /** Partidos de la categoría CON DUEÑO estampado por el padre al resolver
   * su fetch (la primera ronda reconstruye el cuadro línea por línea: match
   * m = líneas 2m-1 y 2m). Solo se usan si el dueño coincide con el scope
   * vigente. Opcional: sin snapshot la tarjeta se comporta como antes (solo
   * acta de bandas). */
  matchesSnapshot?: DrawCeremonyMatchesSnapshot;
  /** Encabezados del PDF del acta. */
  tournamentName?: string | null;
  divisionName?: string | null;
  /** Logo del organizador ya resuelto (uploadAssetHref) para el acta. */
  organizerLogoUrl?: string | null;
}) {
  const [ceremony, setCeremony] = useState<DrawCeremony | null>(null);
  const [plan, setPlan] = useState<DrawCeremonyPlan | null>(null);
  const [scheduledDraft, setScheduledDraft] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Anti-carrera con IDENTIDAD DE SCOPE (4º-5º gates de #135): load y save
  // capturan (torneo+división, seq). Un load aplica solo si es el intento más
  // reciente del scope vigente; un save aplica solo si SU scope sigue vigente
  // — resolviendo tarde tras cambiar de división se DESCARTA sin invalidar
  // nada del scope nuevo; tras el unmount, invalidate() TOTAL: cero
  // mutaciones incluso por la vía isCurrentScope.
  const scopeGuard = useRef(makeScopedLatestOnly()).current;
  const scopeKey = `${tournamentId}:${divisionId ?? "all"}`;
  // Identidad de render (scope + regeneración); la sesión NO va en el string:
  // se compara aparte por IGUALDAD ESTRICTA del token (9º gate — sin hash,
  // cero colisiones, cero escrituras en render). El bloqueo es SINCRÓNICO:
  // en el primer render tras cambiar división/refreshKey/sesión, el load
  // registrado aún es de la identidad/sesión vieja y TODO lo visible queda
  // gateado sin esperar ningún efecto.
  const renderIdentity = `${scopeKey}|${refreshKey ?? ""}`;
  // El resultado del load lleva SU identidad y su desenlace: un load fallido
  // de la identidad vigente muestra su error (no "cargando" eterno); el de
  // otra identidad no muestra nada (7º gate).
  const [loadState, setLoadState] = useState<DrawCeremonyLoadState | null>(null);
  // El estado "guardando" es propiedad del save que lo abrió (token) y de la
  // identidad bajo la que se abrió: otra división nunca hereda el spinner ni
  // queda bloqueada por un save colgado de la anterior, y un save viejo no
  // puede despejar el estado de un save más nuevo.
  const [savingOp, setSavingOp] = useState<{
    token: ScopedToken;
    identity: string;
    sessionToken: string;
  } | null>(null);
  // Ownership atómico de la lista de partidos (8º gate): solo entra al
  // render/PDF si su dueño estampado coincide EXACTAMENTE con el scope
  // vigente (sesión + torneo + división).
  const scopeMatches = useMemo(
    () =>
      drawCeremonyMatchesForScope(matchesSnapshot, {
        token,
        tournamentId,
        divisionId: divisionId ?? null,
      }),
    [matchesSnapshot, token, tournamentId, divisionId],
  );
  // ÚNICA fuente de lo visible/accionable: la derivación pura por identidad.
  const view = deriveDrawCeremonyView({
    renderIdentity,
    sessionToken: token,
    load: loadState,
    savingOp,
    ceremony,
    scheduledDraft,
    message,
    error,
    matchesOwned: scopeMatches.owned,
  });

  const load = useCallback(async (identity: string) => {
    const requestToken = scopeGuard.begin();
    try {
      const response = await api.matches.getDrawCeremony(token, tournamentId, divisionId);
      if (!scopeGuard.isCurrent(requestToken)) return;
      setCeremony(response.data.ceremony);
      setPlan(response.data.plan ?? null);
      setScheduledDraft(toDatetimeLocalValue(response.data.ceremony?.scheduled_at ?? null));
      setLoadState({ identity, sessionToken: token, status: "loaded" });
    } catch {
      if (!scopeGuard.isCurrent(requestToken)) return;
      // Load fallido: draft limpio + ERROR VISIBLE bajo esta identidad (no
      // "cargando" eterno); el guardado queda deshabilitado — el horario
      // viejo no puede enviarse contra este scope.
      setCeremony(null);
      setScheduledDraft("");
      setLoadState({ identity, sessionToken: token, status: "error" });
      setError("No se pudo cargar el acta del sorteo de esta categoría.");
    }
  }, [token, tournamentId, divisionId, scopeGuard]);

  useEffect(() => {
    // Cambio de scope (o regeneración vía refreshKey): entrar al scope
    // invalida TODO lo en vuelo y el formulario se limpia — el horario de A
    // no puede quedar visible ni enviarse bajo B, y el "guardando" de A no
    // se hereda.
    scopeGuard.enterScope(scopeKey);
    setCeremony(null);
    setScheduledDraft("");
    setMessage(null);
    setError(null);
    setLoadState(null);
    setSavingOp(null);
    void load(renderIdentity);
    // Unmount (y cada re-entrada): invalidación TOTAL — una respuesta
    // tardía, load O save, ya no puede mutar nada.
    return () => scopeGuard.invalidate();
  }, [scopeKey, refreshKey, renderIdentity, load, scopeGuard]);

  async function handleSaveSchedule() {
    // Guardia programática (6º gate): aunque algo invoque el handler
    // saltándose el disabled, sin identidad cargada (o con un save en
    // vuelo) no se toca la API.
    if (!view.canSave) return;
    setMessage(null);
    setError(null);
    // El save captura el scope vigente: si el admin cambia de división antes
    // de que resuelva, la respuesta se DESCARTA por completo (sin invalidar
    // el load del scope nuevo).
    const saveToken = scopeGuard.begin();
    setSavingOp({ token: saveToken, identity: renderIdentity, sessionToken: token });
    try {
      const response = await api.matches.scheduleDrawCeremony(token, tournamentId, {
        division_id: divisionId,
        scheduled_at: scheduledDraft ? scheduledDraft : null,
      });
      if (!scopeGuard.isCurrentScope(saveToken)) return;
      setCeremony(response.data.ceremony);
      setMessage(
        scheduledDraft
          ? "Hora del sorteo anunciada (hora CDMX): los jugadores la verán en su torneo."
          : "Hora del sorteo retirada.",
      );
    } catch (err) {
      if (!scopeGuard.isCurrentScope(saveToken)) return;
      setError(err instanceof ApiError ? err.message : "No se pudo guardar la hora del sorteo.");
    } finally {
      // Tras unmount o cambio de scope: CERO mutaciones (isCurrentScope ya
      // murió por invalidate/enterScope). Dentro del scope vigente, solo el
      // dueño despeja su propio estado — un save viejo no puede apagar el
      // spinner de un save más nuevo.
      if (scopeGuard.isCurrentScope(saveToken)) {
        setSavingOp((prev) => (prev !== null && prev.token === saveToken ? null : prev));
      }
    }
  }

  // Cuadro completo línea por línea (pedido de Eduardo 2026-07-22): se
  // reconstruye de los partidos de la primera ronda ya cargados en la
  // página + el acta (bandas de sembrados). Cero llamadas extra al backend.
  // Usa el acta GATEADA por identidad Y los partidos GATEADOS por dueño:
  // ceremonia y matches solo se combinan cuando ambos pertenecen al scope
  // vigente — el acta mixta (líneas de A bajo ceremonia de B, o viceversa)
  // es inconstruible en cualquier frame.
  const drawLines = useMemo(
    () => buildDrawCeremonyLines(scopeMatches.matches, view.ceremony),
    [scopeMatches, view.ceremony],
  );
  const showDrawLines = !!view.ceremony?.performed_at && (drawLines.lines.length > 0 || drawLines.preliminaryLines.length > 0);

  function handlePrintActa() {
    // Guardia programática (6º gate): sin identidad cargada no se imprime —
    // un acta de A no puede salir con encabezado/partidos de B.
    const ceremony = view.ceremony;
    if (!view.canPrint || !ceremony?.performed_at) return;
    setError(null);
    const bands = ceremony.payload?.bands ?? [];
    const sections: string[] = [];
    sections.push(`
      <p><strong>Sorteo realizado</strong> el ${escapeHtml(formatDateTime(ceremony.performed_at))} h (CDMX) · ${escapeHtml(ceremonyMethodLabel(ceremony.method))}${ceremony.seed_count ? ` · ${ceremony.seed_count} sembrado(s) en cuadro de ${ceremony.draw_size ?? "—"}` : ""}</p>
      ${ceremony.payload?.standard ? `<p class="print-muted">${escapeHtml(ceremony.payload.standard)}</p>` : ""}`);
    if (bands.length > 0) {
      const bandRows = bands
        .flatMap((band) =>
          band.seeds.map(
            (row) => `<tr>
          <td>${escapeHtml(band.band)} · ${band.drawn ? "sorteada" : "fija"}</td>
          <td>#${row.seed}</td>
          <td>${escapeHtml(row.display_name)}</td>
          <td>${row.line ?? "—"}</td>
        </tr>`,
          ),
        )
        .join("");
      sections.push(`
      <h2 class="print-section-title">Bandas de sembrados</h2>
      <table>
        <thead><tr><th>Banda</th><th>Sembrado</th><th>Jugador</th><th>Línea del cuadro</th></tr></thead>
        <tbody>${bandRows}</tbody>
      </table>`);
    }
    const printableLineRows = (lines: typeof drawLines.lines) => lines
      .map(
        (line) => `<tr>
        <td>${line.line}</td>
        <td>${escapeHtml(drawLinePlayerText(line))}</td>
        <td>${line.seed !== null ? `#${line.seed}` : "—"}</td>
        <td>${escapeHtml(drawLineBandText(line, drawLines.hasActaBands))}</td>
      </tr>`,
      )
      .join("");
    if (drawLines.mainBracketPending) {
      sections.push(`
      <p class="print-note">El cuadro principal aún no se genera: solo existe la ronda preliminar/inicial parcial.</p>`);
    }
    if (drawLines.preliminaryLines.length > 0) {
      // La preliminar/ronda inicial parcial NO es el cuadro completo (los
      // pases directos no juegan esa ronda): se imprime aparte (gate de #135).
      sections.push(`
      <h2 class="print-section-title">Ronda preliminar / inicial parcial${drawLines.preliminaryRoundLabel ? ` (${escapeHtml(drawLines.preliminaryRoundLabel)})` : ""}</h2>
      <table>
        <thead><tr><th>Línea</th><th>Jugador</th><th>Sembrado</th><th>Banda</th></tr></thead>
        <tbody>${printableLineRows(drawLines.preliminaryLines)}</tbody>
      </table>`);
    }
    if (drawLines.lines.length > 0) {
      sections.push(`
      <h2 class="print-section-title">Cuadro completo línea por línea</h2>
      <table>
        <thead><tr><th>Línea</th><th>Jugador</th><th>Sembrado</th><th>Banda</th></tr></thead>
        <tbody>${printableLineRows(drawLines.lines)}</tbody>
      </table>`);
    }
    if (ceremony.payload?.unseeded_count) {
      sections.push(
        `<p class="print-muted">${ceremony.payload.unseeded_count} jugador(es) no sembrado(s) entraron 100% por sorteo en las líneas restantes.</p>`,
      );
    }
    try {
      openPrintWindow({
        title: `Acta del sorteo · ${tournamentName ?? "Torneos123"}`,
        eyebrow: "Acta del sorteo",
        heading: tournamentName ?? "Torneos123",
        subtitle: divisionName ?? undefined,
        bodyHtml: sections.join(""),
        organizerSlot: true,
        organizerLogoUrl: organizerLogoUrl ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la impresión.");
    }
  }

  // Torneo sin sorteo público (casilla de Datos apagada, Eduardo 2026-10-15):
  // la tarjeta queda plegada; no hay acta pública ni aviso a jugadores.
  if (plan && !plan.enabled) {
    return (
      <section className="card draw-ceremony draw-ceremony--private" aria-label="Sorteo del cuadro">
        <div className="draw-ceremony__plan">
          <div>
            <span className="draw-ceremony__plan-label">Sorteo del cuadro</span>
            <strong>Sin sorteo público</strong>
            <small className="muted">Actívalo en Datos si quieres anunciar un sorteo a los jugadores.</small>
          </div>
          <Link className="btn btn--ghost btn--compact" to={`/admin/tournaments/${tournamentId}/edit`}>
            Ir a Datos
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="card draw-ceremony" aria-label="Sorteo del cuadro">
      <div className="card__header-row">
        <div>
          <h3 className="card__title">Sorteo del cuadro</h3>
          <p className="card__subtitle">
            Estándar internacional: cuadro de 64 → 16 sembrados (1 arriba, 2 abajo, 3-4 sorteados en
            cuartos, 5-8 en octavos, 9-16 en dieciseisavos); los demás entran por sorteo. Anuncia la
            hora y los jugadores verán hora y resultado.
          </p>
        </div>
        {view.canPrint ? (
          <button type="button" className="btn btn--compact btn--ghost" onClick={handlePrintActa}>
            🖨 Imprimir acta / PDF
          </button>
        ) : null}
      </div>
      {plan ? (
        /* Sorteo desde el Paso 1 (Eduardo 2026-09-11): aquí solo se ve; la
           fecha se cambia en Datos. */
        <div className="draw-ceremony__plan">
          <div>
            <span className="draw-ceremony__plan-label">Sorteo público</span>
            <strong>
              {plan.enabled
                ? plan.scheduled_at
                  ? `${formatDateTime(plan.scheduled_at)} h (hora CDMX)`
                  : "Fecha y hora por anunciar"
                : "Sin sorteo público"}
            </strong>
            <small className="muted">
              {plan.enabled
                ? "Los jugadores lo ven en su torneo y en la convocatoria."
                : "Actívalo en Datos si quieres anunciar un sorteo a los jugadores."}
            </small>
          </div>
          <Link className="btn btn--ghost btn--compact" to={`/admin/tournaments/${tournamentId}/edit`}>
            Cambiar fecha del sorteo
          </Link>
        </div>
      ) : (
      <div className="draw-ceremony__schedule">
        <label>
          Hora anunciada del sorteo (hora de la Ciudad de México)
          {/* Bloqueo SINCRÓNICO por identidad (6º gate): sin identidad
              cargada el input muestra vacío y queda deshabilitado — el
              horario de otra división/sesión no se ve ni se edita ni un
              frame. */}
          <input
            type="datetime-local"
            value={view.scheduledDraft}
            onChange={(event) => setScheduledDraft(event.target.value)}
            disabled={!view.scopeLoaded}
          />
        </label>
        <button className="btn btn--ghost" type="button" onClick={() => void handleSaveSchedule()} disabled={!view.canSave}>
          {view.saving ? "Guardando…" : "Guardar hora"}
        </button>
      </div>
      )}
      {view.message ? <p className="form-message form-message--ok">{view.message}</p> : null}
      {view.error ? <p className="form-message form-message--error">{view.error}</p> : null}
      {view.ceremony ? <DrawCeremonyResult ceremony={view.ceremony} /> : view.scopeLoaded ? (
        <p className="muted">Aún no hay sorteo registrado para esta categoría: se registra automáticamente al generar el draw.</p>
      ) : view.loading ? (
        <p className="muted">Cargando acta del sorteo…</p>
      ) : null}
      {showDrawLines ? (
        <details className="draw-ceremony__lines">
          <summary>
            Ver resultados del sorteo{" "}
            <small>
              {drawLines.mainBracketPending
                ? `ronda preliminar/parcial · ${drawLines.preliminaryLines.length / 2} partido(s) · cuadro principal pendiente`
                : `cuadro completo · ${drawLines.lines.length} línea(s)${
                    drawLines.preliminaryLines.length > 0
                      ? ` · preliminar ${drawLines.preliminaryLines.length / 2} partido(s)`
                      : ""
                  }`}
              {drawLines.seededCount ? ` · ${drawLines.seededCount} sembrado(s)` : ""}
            </small>
          </summary>
          {drawLines.preliminaryLines.length > 0 ? (
            <div className="responsive-table-wrap">
              {/* La preliminar NO es el cuadro completo (los pases directos no
                  juegan esa ronda): se lista aparte (gate de #135). */}
              <table className="data-table draw-ceremony__table">
                <thead>
                  <tr>
                    <th className="data-table__cell--num">Preliminar · Línea</th>
                    <th>Jugador</th>
                    <th>Sembrado</th>
                    <th>Banda</th>
                  </tr>
                </thead>
                <tbody>
                  {drawLines.preliminaryLines.map((line) => (
                    <tr key={`prelim-${line.line}`}>
                      <td className="data-table__cell--num">{line.line}</td>
                      <td>{drawLinePlayerText(line)}</td>
                      <td>{line.seed !== null ? `#${line.seed}` : "—"}</td>
                      <td>{drawLineBandText(line, drawLines.hasActaBands)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {drawLines.mainBracketPending ? (
            <p className="muted">
              El cuadro principal aún no se genera: solo existe la ronda preliminar/inicial parcial.
            </p>
          ) : null}
          {drawLines.lines.length > 0 ? (
            <div className="responsive-table-wrap">
              <table className="data-table draw-ceremony__table">
                <thead>
                  <tr>
                    <th className="data-table__cell--num">Línea</th>
                    <th>Jugador</th>
                    <th>Sembrado</th>
                    <th>Banda</th>
                  </tr>
                </thead>
                <tbody>
                  {drawLines.lines.map((line) => (
                    <tr key={line.line}>
                      <td className="data-table__cell--num">{line.line}</td>
                      <td>{drawLinePlayerText(line)}</td>
                      <td>{line.seed !== null ? `#${line.seed}` : "—"}</td>
                      <td>{drawLineBandText(line, drawLines.hasActaBands)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </details>
      ) : null}
    </section>
  );
}
